import { bool, node, number, string } from "prop-types";
import styled, { css } from "styled-components/macro";
import { rem } from "polished";

const baseStyles = css`
  margin-bottom: ${({ help, labelOffset }) => {
    if (!!help) {
      return rem("34px");
    }

    switch (labelOffset) {
      case "largeTopOnly":
      case "none":
      case "smaller":
        return 0;
      case "largeBottomOnly":
        return rem("24px");
      default:
        return rem("20px");
    }
  }};
  padding-top: ${({ labelOffset }) => {
    switch (labelOffset) {
      case "smaller":
        return rem("13px");
      case "small":
        return rem("16px");
      case "medium":
        return rem("20px");
      case "largeTopOnly":
        return rem("24px");
      case "none":
      default:
        return 0;
    }
  }};
  position: relative;
  width: ${({ width }) => (!!width ? width : "100%")};

  input[type="text"] ~ input[type="text"] {
    margin-top: ${rem("10px")};
  }
`;

const horizontalStyles = css`
  column-gap: ${rem("12px")};
  display: grid;
  grid-template-columns: 30% ${({ $horizontalInputWidth }) =>
      $horizontalInputWidth
        ? `minmax(auto, ${rem($horizontalInputWidth + "px")})`
        : "1fr"};
  grid-template-rows: auto;

  // Assumes label is the first child
  > :first-child {
    align-self: center;
    grid-column: 1 / 2;
    grid-row: 1 / 2;
  }

  // Assumes input is the second child
  > :nth-child(2) {
    grid-column: 2 / 3;
    grid-row: 1 / 2;
  }

  // Everything else gets added under the input
  // in the order it was added
  ${[...Array(5).keys()].map(
    i => `
    > :nth-child(${i + 3}) {
      grid-column: 2 / 3;
      grid-row: ${i + 3} / ${i + 4};
    }
  `
  )}
`;

const Error = styled.div`
  position: relative;

  > span {
    color: ${({ theme }) => theme.error.color};
    font-size: ${rem("10px")};
    line-height: 1;
    padding-top: ${rem("5px")};
    position: absolute;
    white-space: nowrap;
  }
`;

const Help = styled.div`
  position: relative;

  > span {
    font-size: ${rem("10px")};
    line-height: 1;
    padding-top: ${rem("5px")};
    position: absolute;
    right: ${({ hasError }) => (hasError ? 0 : "auto")};
    white-space: nowrap;
  }
`;

const Group = styled(
  ({ children, error, help, labelOffset, width, ...rest }) => (
    <div {...rest}>
      {children}
      {!!error && (
        <Error>
          <span>{error}</span>
        </Error>
      )}
      {!!help && (
        <Help hasError={!!error}>
          <span>{help}</span>
        </Help>
      )}
    </div>
  )
).attrs(({ error }) => ({
  className: !!error ? "has-error" : null,
}))`
  ${baseStyles};
  ${({ $horizontal }) => $horizontal && horizontalStyles}
`;

Group.propTypes = {
  error: string,
  help: node,
  labelOffset: string,
  width: string,
  $horizontal: bool, // props prefixed with $ won't be passed to DOM
  $horizontalInputWidth: number,
};

export default Group;
